/** CtaSettings -- CTA preferences for outreach. PUT /api/settings/cta. */

import { useState, useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2, CheckCircle2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { FieldHint } from '@/components/field-hint'
import { useApi } from '@/hooks/use-api'
import type { CtaConfig, CtaType } from '@dealcloser/shared'
import { DEFAULT_CTA } from '@dealcloser/shared'

// Fixed CTA options — users pick from this list rather than typing a
// custom label. Keeps outbound messaging on-brand and avoids surprise
// CTAs that we haven't trained the AI on.
const CTA_TYPE_OPTIONS: readonly { value: CtaType; label: string }[] = [
  { value: 'book_demo', label: 'Book a Demo' },
  { value: 'book_discovery_call', label: 'Book a Discovery Call' },
  { value: 'book_strategy_call', label: 'Book a Strategy Call' },
  { value: 'free_audit', label: 'Free Audit' },
  { value: 'send_to_page', label: 'Send to Landing Page' },
  { value: 'send_to_trial', label: 'Send to Free Trial' },
  { value: 'send_to_event', label: 'Send to Event' },
  { value: 'direct_conversation', label: 'Direct Conversation' },
  { value: 'calendar_link', label: 'Calendar Link' },
]

const ctaKeys = { config: ['settings', 'cta'] as const }

export default function CtaSettings() {
  const queryClient = useQueryClient()
  const apiFetch = useApi()
  const [form, setForm] = useState<CtaConfig>(DEFAULT_CTA)
  const [savedSuccess, setSavedSuccess] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey: ctaKeys.config,
    queryFn: async (): Promise<CtaConfig> => {
      const res = await apiFetch<CtaConfig>('/api/settings/cta')
      if (!res.success || !res.data) return DEFAULT_CTA
      return res.data
    },
    staleTime: 5 * 60_000,
  })

  useEffect(() => { if (data) setForm(data) }, [data])

  const saveMutation = useMutation({
    mutationFn: async (config: CtaConfig): Promise<void> => {
      const res = await apiFetch('/api/settings/cta', { method: 'PUT', body: config })
      if (!res.success) throw new Error(res.error ?? 'Failed to save')
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ctaKeys.config })
      setSavedSuccess(true)
      setTimeout(() => setSavedSuccess(false), 3000)
    },
  })

  const set = <K extends keyof CtaConfig>(key: K) => (v: CtaConfig[K]) =>
    setForm((prev) => ({ ...prev, [key]: v }))

  const isSaving = saveMutation.isPending
  const saveError = saveMutation.error instanceof Error ? saveMutation.error.message : null
  const disabled = isLoading || isSaving

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Call to Action</CardTitle>
          <CardDescription>
            Pick the CTA you want the AI to drive toward, then paste the link prospects should land on. The AI sends this link automatically when a lead is ready to schedule.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <div className="flex items-center gap-1.5">
              <Label htmlFor="cta-type">CTA Type</Label>
              <FieldHint label="What does CTA Type do?">
                Picks the goal the AI drives toward when a lead is closing. "Book a Demo" shapes asks differently than "Free Audit" or "Send to Landing Page" — the AI uses this to phrase invitations.
              </FieldHint>
            </div>
            <Select value={form.ctaType} onValueChange={(v) => set('ctaType')(v as CtaType)} disabled={disabled}>
              <SelectTrigger id="cta-type"><SelectValue placeholder="Select a CTA type" /></SelectTrigger>
              <SelectContent>
                {CTA_TYPE_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <div className="flex items-center gap-1.5">
              <Label htmlFor="cta-primary-url">Meeting / CTA Link</Label>
              <FieldHint label="When does the AI send this link?">
                The URL the AI sends when a lead replies showing clear scheduling intent ("yes let's hop on a call", "send me a time", etc.). Pasted verbatim into the reply on the same turn. Leave blank if you don't want auto-link sending.
              </FieldHint>
            </div>
            <Input id="cta-primary-url" type="url" placeholder="https://calendly.com/..." value={form.ctaPrimaryUrl} onChange={(e) => set('ctaPrimaryUrl')(e.target.value)} disabled={disabled} />
          </div>
        </CardContent>
      </Card>

      <div className="flex items-center gap-3">
        <Button onClick={() => saveMutation.mutate(form)} disabled={disabled}>
          {isSaving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving...</> : 'Save CTA Settings'}
        </Button>
        {savedSuccess && (
          <div className="flex items-center gap-1.5 text-sm text-emerald-300 ">
            <CheckCircle2 className="h-4 w-4" />Saved successfully
          </div>
        )}
        {saveError && <p className="text-sm text-destructive">{saveError}</p>}
      </div>
    </div>
  )
}
